import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, DollarSign, CalendarClock, Bell, MapPin, Clock, Plus, Send, CreditCard, Star, ListChecks, Receipt, ArrowRight, RefreshCw } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";

const card = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";

export default function Overview() {
  const [o, setO] = useState(null);
  const [a, setA] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    api.get("/admin/overview").then((r) => setO(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  const manualRefresh = () => { setRefreshing(true); load(); setTimeout(() => setRefreshing(false), 700); };

  const stats = o ? [
    { label: "Revenue Today", value: `$${o.revenue_today.toFixed(2)}`, icon: DollarSign },
    { label: "Revenue This Week", value: `$${o.revenue_week.toFixed(2)}`, icon: DollarSign },
    { label: "Revenue This Month", value: `$${o.revenue_month.toFixed(2)}`, icon: DollarSign },
    { label: "Registered Users", value: o.registered_users, icon: Users },
    { label: "New Sign-ups Today", value: o.new_signups_today, icon: UserPlus },
    { label: "Pending Bookings", value: o.pending_bookings, icon: CalendarClock },
    { label: "Instructor Approvals", value: o.pending_instructor_approvals, icon: Bell },
    { label: "Today's Classes", value: (o.todays_classes || []).length, icon: MapPin },
  ] : [];

  const todays = o?.todays_classes || [];
  const unpaid = o?.unpaid_invoices || [];
  const waitlist = o?.waitlist || [];

  return (
    <div data-testid="overview-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Home Dashboard</h1>
          <p className="text-brand-ink/60 dark:text-white/50 mt-2">Everything happening across Paws N Poses today.</p>
        </div>
        <button onClick={manualRefresh} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-line dark:border-neutral-700 text-sm hover:border-brand-red transition-colors" data-testid="overview-refresh" title="Refresh (auto every 30s)">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Quick actions */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/dashboard/schedule" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-red text-white text-sm font-medium hover:bg-brand-reddark transition-colors" data-testid="qa-new-class"><Plus className="w-4 h-4" /> New class</Link>
        <Link to="/dashboard/messaging" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-line dark:border-neutral-700 text-sm font-medium hover:border-brand-red transition-colors" data-testid="qa-broadcast"><Send className="w-4 h-4" /> Send announcement</Link>
        <Link to="/dashboard/payments" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-line dark:border-neutral-700 text-sm font-medium hover:border-brand-red transition-colors" data-testid="qa-payments"><CreditCard className="w-4 h-4" /> Payments</Link>
        <Link to="/dashboard/reviews" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-line dark:border-neutral-700 text-sm font-medium hover:border-brand-red transition-colors" data-testid="qa-reviews"><Star className="w-4 h-4" /> Reviews</Link>
      </div>

      {/* At-a-glance widgets */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Widget title="Today's Classes" icon={MapPin} to="/dashboard/schedule" count={todays.length} testid="widget-today">
          {todays.length === 0 ? <Empty text="No classes scheduled today." /> : todays.slice(0, 5).map((s) => (
            <Row key={s.id} left={s.park_name} right={`${s.time} · ${Math.max(0, s.capacity - (s.spots_taken || 0))}/${s.capacity} open`} testid={`w-today-${s.id}`} />
          ))}
        </Widget>
        <Widget title="Unpaid Orders" icon={Receipt} to="/dashboard/invoices" count={unpaid.length} testid="widget-unpaid">
          {unpaid.length === 0 ? <Empty text="Everything's paid up 🎉" /> : unpaid.slice(0, 5).map((i) => (
            <Row key={i.id} left={`${i.number} · ${i.client_name || ""}`} right={`$${(i.total || 0).toFixed(2)}`} testid={`w-unpaid-${i.id}`} />
          ))}
        </Widget>
        <Widget title="Waitlist Activity" icon={ListChecks} to="/dashboard/waitlist" count={waitlist.length} testid="widget-waitlist">
          {waitlist.length === 0 ? <Empty text="No one waiting right now." /> : waitlist.slice(0, 5).map((w) => (
            <Row key={w.id} left={w.name} right={`${w.park_name || ""} ${w.date || ""}`} testid={`w-waitlist-${w.id.slice(0, 8)}`} />
          ))}
        </Widget>
      </div>

      {/* Stats */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((c) => (
          <div key={c.label} className={`${card} p-5`} data-testid={`stat-${c.label.toLowerCase().split(" ")[0]}`}>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.15em] text-brand-ink/50 dark:text-white/40">{c.label}</span><c.icon className="w-5 h-5 text-brand-red" /></div>
            <div className="mt-3 font-display text-3xl">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className={`${card} p-6`}>
          <h2 className="font-display text-xl mb-4">Revenue (6 months)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={a?.revenue_by_month || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#8883" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Line type="monotone" dataKey="revenue" stroke="#D42A2A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className={`${card} p-6`}>
          <h2 className="font-display text-xl mb-4">Bookings by month</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={a?.attendance_by_month || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#8883" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="bookings" fill="#D42A2A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Today + Notifications */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className={`${card} p-6 lg:col-span-2`}>
          <h2 className="font-display text-xl mb-4">Upcoming events</h2>
          {!o || o.upcoming_events.length === 0 ? <p className="text-brand-ink/50 dark:text-white/40">No upcoming sessions.</p> : (
            <div className="divide-y divide-brand-line dark:divide-neutral-800">
              {o.upcoming_events.map((s) => (
                <div key={s.id} className="py-3 flex items-center justify-between" data-testid={`upcoming-${s.id}`}>
                  <div><div className="font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-red" /> {s.park_name}</div><div className="text-sm text-brand-ink/50 dark:text-white/40 flex items-center gap-1 mt-1"><Clock className="w-3.5 h-3.5" /> {s.date} {s.time}</div></div>
                  <span className="text-sm text-brand-ink/50 dark:text-white/40">{Math.max(0, s.capacity - (s.spots_taken || 0))}/{s.capacity} open</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={`${card} p-6`}>
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Bell className="w-5 h-5 text-brand-red" /> Notifications</h2>
          {!o || o.notifications.length === 0 ? <p className="text-brand-ink/50 dark:text-white/40">You're all caught up 🎉</p> : (
            <div className="space-y-3">
              {o.notifications.map((n, i) => (
                <div key={i} className="text-sm bg-brand-mist dark:bg-neutral-800 rounded-lg px-4 py-3" data-testid={`notif-${i}`}>{n.text}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Widget = ({ title, icon: Icon, to, count, children, testid }) => (
  <div className={`${card} p-5`} data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-display text-lg flex items-center gap-2"><Icon className="w-4 h-4 text-brand-red" /> {title} {count ? <span className="text-xs font-sans bg-brand-red text-white rounded-full px-2 py-0.5">{count}</span> : null}</h2>
      <Link to={to} className="text-brand-ink/40 dark:text-white/40 hover:text-brand-red transition-colors"><ArrowRight className="w-4 h-4" /></Link>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);
const Row = ({ left, right, testid }) => (
  <div className="flex items-center justify-between text-sm py-1.5 border-b border-brand-line dark:border-neutral-800 last:border-0" data-testid={testid}>
    <span className="truncate mr-2">{left}</span><span className="text-brand-ink/50 dark:text-white/40 shrink-0">{right}</span>
  </div>
);
const Empty = ({ text }) => <p className="text-sm text-brand-ink/50 dark:text-white/40">{text}</p>;
